<template>
  <CContainer class="datatable-container substitue-datatable pb-2">
    <!-- Archive message -->
    <CModal
      backdrop="static"
      :visible="!!showSubstituteUsersModal"
      :close-on-backdrop="false"
    >
      <template #header-wrapper>
        <div
          class="pt-4 px-4 create-popup-header d-flex align-items-center justify-content-between"
        >
          <div
            class="d-inline-block bg-gradient-blue text-white rounded-circle p-1 lh-normal cursor-pointer"
            @click=";(showSubstituteUsersModal = null), resetResponseMessages()"
          >
            <span class="close-icon" />
          </div>
        </div>
      </template>
      <div class="px-3 pt-3 pb-1">
        <div>
          {{ reqResponse.message }}
        </div>
        <div class="text-center mt-3">
          <button
            @click=";(showSubstituteUsersModal = null), resetResponseMessages()"
            class="btn p-3 bg-gradient-blue text-white ms-2"
          >
            Okay
          </button>
        </div>
      </div>
      <template #footer-wrapper>
        <div />
      </template>
    </CModal>
    <!-- End Modals -->
    <Loader :is-processing="isLoading" />
    <CRow class="mt-5">
      <CCol md="10" class="m-auto">
        <div
          class="table-tabs-header mb-2 bg-white d-flex align-items-center justify-content-between"
        >
          <div
            v-for="(tab, index) in tableTabs"
            :key="index"
            class="table-tab"
            :class="{ active: tab.value === activeTableTab.value }"
          >
            {{ tab.label }}
          </div>
          <div class="actions d-flex align-items-center">
            <div
              class="lh-normal cursor-pointer text-accent-blue-light ms-2 ms-sm-0"
              @click="exportUsers()"
            >
              <i class="fi flaticon-download me-1" />
              <span class="d-none d-md-inline-block">Download CSV</span>
            </div>

            <div
              class="d-flex align-items-center search-field mb-0 ms-2 ms-md-4"
            >
              <CInputGroup class="w-auto">
                <div class="input-group-prepend">
                  <span class="input-group-text lh-normal"
                    ><div><i class="flaticon-magnifying-glass"></i></div
                  ></span>
                </div>
                <CFormInput
                  placeholder="Search"
                  v-model="columnSearchKeys.globalQuery"
                  @input="searchByQuery($event.target.value)"
                >
                </CFormInput>
              </CInputGroup>
            </div>
          </div>
        </div>
        <div class="status-guides-container d-block pt-1 pb-3">
          <div class="d-flex justify-content-between align-items-end flex-wrap">
            <div class="ps-3">
              <div class="mb-2">Select all:</div>
              <div class="d-flex align-items-center actions mb-3 mb-sm-0">
                <input
                  type="checkbox"
                  class="me-2"
                  @change="selectUsers($event)"
                />

                <CButton
                  class="me-2 p-small bg-gradient-blue"
                  @click="generatePINS()"
                >
                  <span class="btn bg-white px-1 px-sm-3 py-1 text-uppercase"
                    ><b class="text-graident-blue">Generate pin(s)</b></span
                  >
                </CButton>

                <CButton
                  class="me-2 p-small bg-gradient-blue"
                  @click="bulkDelete()"
                >
                  <span class="btn bg-white px-1 px-sm-3 py-1"
                    ><b class="text-graident-blue text-uppercase"
                      >Inactive teacher(s)</b
                    ></span
                  >
                </CButton>
              </div>
            </div>
            <per-page-selector :action="setPerPage" :pagination="pagination" />
          </div>
        </div>

        <!-- End of modals -->
        <DataTable
          class="datatable remove-first-th-border bg-white substitue-datatable"
          :items="users"
          :fields="dataTableFields"
          :column-filter="true"
          :sorter="{ external: true }"
          :no-items-view="{
            noResults: 'No items available',
            noItems: 'No items available'
          }"
          @update:sorter-value="sortByColumn"
        >
          <template #select="{ item }">
            <td class="mw-auto">
              <input v-model="selectedUsers" type="checkbox" :value="item.id" />
            </td>
          </template>
          <template #pin-header>
            <div class="d-block text-center float-none mb-1">
              <b>PIN</b>
            </div>
            <div class="d-flex align-items-center ms-4">
              <span class="flaticon-hide me-2" />
              <CFormSwitch
                class="modules-switch pins-switch mx-2"
                shape="pill"
                v-model="showPins"
              />
              <span class="flaticon-eye" />
            </div>
          </template>

          <template #pin="{ item }">
            <td>
              <div v-if="item.pin">
                {{ showPins ? item.pin.pin : "******" }}
              </div>
            </td>
          </template>

          <template #action="{ item }">
            <td class="mw-auto">
              <div class="d-flex">
                <button class="btn btn-edit me-2" @click="editUser(item)">
                  <i class="flaticon-edit me-1" /> Edit
                </button>
                <button
                  v-if="item.status"
                  class="btn btn-cancel no-target"
                  @click="toggleSubstitute(item)"
                >
                  Deactivate
                </button>
                <button
                  v-else
                  class="btn btn-restore no-target"
                  @click="toggleSubstitute(item)"
                >
                  <span class="flaticon-back-arrow me-1" /> REACTIVATE
                </button>
              </div>
            </td>
          </template>

          <template #first_name="{ item }">
            <td>
              <b>{{ item.first_name ? item.first_name : "" }}</b>
            </td>
          </template>

          <template #last_name="{ item }">
            <td>
              <b>{{ item.last_name ? item.last_name : "" }}</b>
            </td>
          </template>

          <template #first_name-filter>
            <input
              v-model="columnSearchKeys.first_name.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #last_name-filter>
            <input
              v-model="columnSearchKeys.last_name.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #status="{ item }">
            <td class="text-center">
              <b
                class="limit-status no-target"
                :class="item.status ? 'active' : 'inactive-gray'"
                >{{ item.status ? "active" : "inactive" }}</b
              >
            </td>
          </template>
        </DataTable>

        <Pagination
          v-if="pagination.pages && pagination.pages > 1 && !isLazyLoadingMode"
          class="cs-pagination mt-4"
          :dots="false"
          :double-arrows="true"
          :active-page="pagination.activePage"
          :pages="pagination.pages"
          size="lg"
          align="center"
          @update:active-page="getUsers"
        >
          <template #last-button>
            <i class="flaticon-right-arrow-1" />
          </template>
          <template #first-button>
            <i class="flaticon-left-arrow-2" />
          </template>
          <template #previous-button>
            <i class="fi flaticon-left-arrow-1" />
          </template>
          <template #next-button>
            <i class="fi flaticon-chevron" />
          </template>
        </Pagination>
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import Loader from "@/components/Loader"
import { mapGetters } from "vuex"
import PerPageSelector from "../PerPageSelector.vue"

export default {
  name: "SubstituteUsersDataTable",
  components: {
    Loader,
    PerPageSelector
  },
  data() {
    return {
      showSubstituteUsersModal: null,
      isLoading: false,
      isLazyLoadingMode: false,
      showPins: false,
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: { label: "25", value: 25 }
      },
      selectedUsers: [],
      tableTabs: [
        {
          label: "Substitute Teachers",
          value: "substitute_teachers"
        }
      ],
      activeTableTab: {
        value: "substitute_teachers"
      },
      filterParams: {
        sort_query: "created_at:desc",
        searchQuery: ""
      },
      dataTableFields: [
        { key: "select", label: "", type: "tabular", filter: false },
        {
          key: "action",
          label: "Action",
          type: "tabular",
          filter: false,
          sorter: false
        },
        { key: "first_name", label: "First Name", type: "tabular" },
        { key: "last_name", label: "Last Name", type: "tabular" },
        {
          key: "pin",
          label: "PIN",
          type: "tabular",
          filter: false,
          sorter: false
        },
        { key: "status", label: "Status", type: "tabular", filter: false }
      ],
      columnSearchKeys: {
        globalQuery: "",
        first_name: { value: "", column: ["first_name"] },
        last_name: { value: "", column: ["last_name"] }
      },
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      }
    }
  },
  computed: {
    ...mapGetters({
      users: "users/substituteUsers",
      editableUser: "users/editableUser"
    })
  },
  mounted() {
    this.initLazyLoading()
    this.getUsers()
  },
  methods: {
    initLazyLoading() {
      const body = document.getElementsByClassName("main-scroll-container")[0]
      if (body) {
        body.onscroll = () => {
          if (
            this.isLazyLoadingMode &&
            this.users &&
            this.users.length &&
            this.pagination.total > this.users.length
          ) {
            if (body.offsetHeight + body.scrollTop + 1 >= body.scrollHeight) {
              this.pagination.activePage = this.pagination.activePage + 1
              this.getUsers(this.pagination.activePage, false, true)
            }
          }
        }
      }
    },
    toggleTab(tab) {
      this.tabs[tab] = !this.tabs[tab]
    },
    bulkDelete() {
      this.$store
        .dispatch("users/substituteDeleteBulk", {
          teacher_ids: this.selectedUsers.join()
        })
        .then(() => {
          this.getUsers()
          this.selectedUsers = []
        })
    },
    selectUsers(e) {
      if (e.target.checked) {
        this.selectedUsers = []
        this.users.forEach((user) => {
          this.selectedUsers.push(user.id)
        })
      } else {
        this.selectedUsers = []
      }
    },
    exportUsers() {
      this.isLoading = true
      const params = {
        page: this.pagination.activePage,
        per_page: [25, 50, 100].includes(this.pagination.per_page.value)
          ? this.pagination.per_page.value
          : "all",
        is_substitute: true
      }
      if (this.filterParams.searchQuery) {
        params.search_query = this.filterParams.searchQuery
      }
      if (this.filterParams.sort_query) {
        params.sort = this.filterParams.sort_query
      }
      this.$store
        .dispatch("users/exportUsers", params)
        .then((response) => {
          if (response.data) {
            this.$download.CSVExport(response.data, "substitute_users")
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },

    generatePINS() {
      this.$store
        .dispatch("users/substituteBulkPin", {
          users: this.selectedUsers.join()
        })
        .then(() => {
          this.getUsers()
          this.selectedUsers = []
        })
    },
    getUsers(page, noLoader, isLazyLoadingMode) {
      this.pagination.activePage = page ? page : 1
      this.isLoading = !noLoader
      this.$store
        .dispatch("users/getSubstituteUsers", {
          sort: this.filterParams.sort_query,
          search_query: this.filterParams.searchQuery,
          page: this.pagination.activePage,
          per_page: this.pagination.per_page.value
        })
        .then((response) => {
          const data = response.data
          if (isLazyLoadingMode) {
            this.$store.commit("users/PUSH_USERS", data.data)
          } else {
            this.$store.commit("users/SET_USERS", data.data)
          }
          const meta = data.meta
          if (meta) {
            this.pagination.total = meta.total
            this.pagination.from = meta.from
            this.pagination.to = meta.to
            this.pagination.pages = Math.ceil(meta.total / meta.per_page)
            this.pagination.activePage = Number(this.pagination.activePage)
            this.pagination = JSON.parse(JSON.stringify(this.pagination))
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },
    editUser(user) {
      this.$store.commit("users/SET_EDITABLE_SUBSTITUTE", user)
      this.$helpers.scrollToTop()
    },
    toggleSubstitute(user) {
      this.isLoading = true
      this.$store
        .dispatch(
          `users/${
            user.status
              ? "deactivateSubstituteTeacher"
              : "activateSubstituteTeacher"
          }`,
          { userId: user.id }
        )
        .then(() => {
          this.isLoading = false
          setTimeout(() => {
            this.getUsers(1, true)
          }, 500)
        })
        .catch((err) => {
          this.isLoading = false
          const response = err.response.data
          this.showSubstituteUsersModal = true
          this.setErrorResponse(response.message, response.errors)
        })
    },
    searchByQuery(value, type) {
      clearInterval(this.searchTimeOut)
      this.searchTimeOut = setTimeout(() => {
        this.handleQuerySearch(value, type)
      }, 500)
    },
    handleQuerySearch(value, type) {
      const query = value.toString()
      if (
        query.replace(/\s/g, "").length > 0 ||
        query.replace(/\s/g, "").length === 0
      ) {
        this.filterParams.searchQuery = ""
        if (type && type === "column") {
          this.columnSearchKeys.globalQuery = ""
          for (const key in this.columnSearchKeys) {
            if (Object.hasOwnProperty.call(this.columnSearchKeys, key)) {
              const columnData = this.columnSearchKeys[key]
              if (columnData.value) {
                columnData.column.forEach((col) => {
                  this.filterParams.searchQuery =
                    this.filterParams.searchQuery +
                    `${col}:"${columnData.value}", `
                })
              }
            }
          }
          this.filterParams.searchQuery = this.filterParams.searchQuery.slice(
            0,
            -2
          )
        } else {
          this.resetSearchQuery()
          this.columnSearchKeys.globalQuery = query
          this.filterParams.searchQuery = query ? '"' + query + '"' : query
        }
        this.getUsers()
      }
    },
    sortByColumn(column) {
      const columnName = this.columnSearchKeys[column.column]
        ? this.columnSearchKeys[column.column].column[0]
        : column.column
      this.filterParams.sort_query = `${columnName}:${
        column.asc ? "asc" : "desc"
      }`
      this.getUsers()
    },
    resetSearchQuery() {
      this.filterParams.searchQuery = ""
      this.columnSearchKeys = {
        globalQuery: "",
        first_name: { value: "", column: ["first_name"] },
        last_name: { value: "", column: ["last_name"] }
      }
    },
    setPerPage(option) {
      if (option) {
        this.isLazyLoadingMode = option.label === "All entries"
        this.pagination.per_page = option
        this.getUsers()
      }
    },
    refreshDataTable() {
      this.resetSearchQuery()
      this.getUsers(1, true, false)
    },
    resetResponseMessages() {
      this.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    },
    setErrorResponse(message, errors) {
      this.reqResponse = {
        type: "danger",
        message: message ? message : "Something went wrong!",
        errors: errors ? errors : {}
      }
    }
  }
}
</script>
